import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type {
  WorkflowDetail,
  WorkflowDraft,
  WorkflowLogs,
  WorkflowStats,
  WorkflowSummary,
} from '@internal/plugin-workflow-common';

export interface WorkflowApi {
  list(): Promise<WorkflowSummary[]>;
  stats(): Promise<WorkflowStats>;
  get(name: string): Promise<WorkflowDetail>;
  logs(name: string, nodeId: string): Promise<WorkflowLogs>;
  preview(draft: WorkflowDraft): Promise<{ errors: string[]; manifest?: string }>;
  submit(draft: WorkflowDraft): Promise<{ name: string; namespace: string }>;
  resubmit(name: string): Promise<{ name: string }>;
  stop(name: string): Promise<void>;
  remove(name: string): Promise<void>;
}

export const workflowApiRef = createApiRef<WorkflowApi>({
  id: 'plugin.workflow.service',
});

export class WorkflowClient implements WorkflowApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  /** Unwraps Backstage's error envelope so the real reason reaches the UI. */
  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const base = await this.discoveryApi.getBaseUrl('workflow');
    const res = await this.fetchApi.fetch(`${base}${path}`, init);
    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        if (body?.error?.message) detail = body.error.message;
      } catch {
        // Non-JSON body; the status line is all we have.
      }
      throw new Error(detail);
    }
    return (await res.json()) as T;
  }

  private post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  async list() {
    return (await this.request<{ items: WorkflowSummary[] }>('/workflows')).items;
  }

  async stats() {
    return this.request<WorkflowStats>('/stats');
  }

  async get(name: string) {
    return this.request<WorkflowDetail>(
      `/workflows/${encodeURIComponent(name)}`,
    );
  }

  async logs(name: string, nodeId: string) {
    return this.request<WorkflowLogs>(
      `/workflows/${encodeURIComponent(name)}/nodes/${encodeURIComponent(
        nodeId,
      )}/logs`,
    );
  }

  async preview(draft: WorkflowDraft) {
    return this.post<{ errors: string[]; manifest?: string }>('/preview', draft);
  }

  async submit(draft: WorkflowDraft) {
    return this.post<{ name: string; namespace: string }>('/workflows', draft);
  }

  async resubmit(name: string) {
    return this.post<{ name: string }>(
      `/workflows/${encodeURIComponent(name)}/resubmit`,
    );
  }

  async stop(name: string) {
    await this.post(`/workflows/${encodeURIComponent(name)}/stop`);
  }

  async remove(name: string) {
    await this.request(`/workflows/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    });
  }
}
